import { useMemo } from 'react';
import { getToken } from './tokenAccess';
import { identityFromToken, type Identity } from './claims';
import { useAuthState } from './authState';

/**
 * The logged-in identity, derived from the bearer token's claims. AITRAS has no
 * `/me` endpoint, so name/email/tenant/initials come from the decoded ID token.
 *
 * The token is seeded before first render (dev/msw modes, and OIDC `primeAccessToken`
 * / userManager events), so a synchronous read is sufficient here — recomputed when
 * auth readiness flips. This mirrors the non-reactive read used by `useIsAdmin`.
 */
export function useIdentity(): { user: Identity | undefined } {
  const { isAuthenticated, isLoading } = useAuthState();
  const ready = isAuthenticated && !isLoading;
  const user = useMemo(
    () => (ready ? identityFromToken(getToken()) ?? undefined : undefined),
    [ready],
  );
  return { user };
}
